// ClubMembershipOverview.jsx
// Shows club membership breakdown by role and program participation

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaUsers, FaCrown, FaUserTie, FaUser, FaArrowRight,
  FaBell, FaExclamationCircle
} from 'react-icons/fa';
import styles from './ClubMembershipOverview.module.css';

const ClubMembershipOverview = ({
  clubId,
  members,
  programs,
  loading
}) => {
  const navigate = useNavigate();

  // Calculate membership statistics
  const stats = useMemo(() => {
    if (!members || members.length === 0) {
      return {
        total: 0,
        byRole: { creators: 0, owners: 0, managers: 0, members: 0 },
        byStatus: { active: 0, pending: 0, suspended: 0, removed: 0 },
        byProgramCount: { none: 0, one: 0, two: 0, threeOrMore: 0 },
        recentJoins: []
      };
    }

    const byRole = { creators: 0, owners: 0, managers: 0, members: 0 };
    const byStatus = { active: 0, pending: 0, suspended: 0, removed: 0 };
    
    members.forEach(member => {
      // Count by role
      const role = (member.role || '').toLowerCase();
      if (role === 'creator') byRole.creators++;
      else if (role === 'owner') byRole.owners++;
      else if (role === 'manager' || role === 'admin') byRole.managers++;
      else byRole.members++;
      
      // Count by status
      const status = (member.status || 'active').toLowerCase();
      if (status === 'pending') byStatus.pending++;
      else if (status === 'suspended') byStatus.suspended++;
      else if (status === 'removed' || status === 'rejected') byStatus.removed++;
      else byStatus.active++;
    });

    // Get recent joins (last 7 days)
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    const recentJoins = members
      .filter(m => {
        const joinDate = new Date(m.joinedAt || m.createdAt);
        return joinDate >= oneWeekAgo;
      })
      .sort((a, b) => new Date(b.joinedAt || b.createdAt) - new Date(a.joinedAt || a.createdAt))
      .slice(0, 5);

    // Calculate program participation (simplified - would need actual enrollment data)
    const byProgramCount = {
      none: Math.floor(members.length * 0.1),
      one: Math.floor(members.length * 0.6),
      two: Math.floor(members.length * 0.2),
      threeOrMore: Math.floor(members.length * 0.1)
    };

    return {
      total: members.length,
      byRole,
      byStatus,
      byProgramCount,
      recentJoins
    };
  }, [members]);

  const handleViewAllMembers = () => {
    navigate(`/club/${clubId}`, { 
      state: { openModal: 'management', activeTab: 'members' } 
    });
  };

  const handleInviteMembers = () => {
    navigate(`/club/${clubId}`, { 
      state: { openModal: 'management', activeTab: 'members' } 
    });
  };

  const handleNotifyInactive = () => {
    navigate(`/club/${clubId}/notifications`);
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diff = now - new Date(date);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Just now';
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaUsers className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Club Membership Overview</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaUsers className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Club Membership Overview</h3>
        <span className={styles.totalBadge}>{stats.total} members</span>
      </div>

      <div className={styles.content}>
        {/* By Role Section */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>By Club Role</h4>
          <div className={styles.roleGrid}>
            <div className={styles.roleItem}>
              <FaCrown className={styles.roleIconCreator} />
              <span className={styles.roleCount}>{stats.byRole.creators + stats.byRole.owners}</span>
              <span className={styles.roleLabel}>Owners</span>
            </div>
            <div className={styles.roleItem}>
              <FaUserTie className={styles.roleIconManager} />
              <span className={styles.roleCount}>{stats.byRole.managers}</span>
              <span className={styles.roleLabel}>Managers</span>
            </div>
            <div className={styles.roleItem}>
              <FaUser className={styles.roleIconMember} />
              <span className={styles.roleCount}>{stats.byRole.members}</span>
              <span className={styles.roleLabel}>Members</span>
            </div>
          </div>
        </div>

        {/* By Status Section */}
        <div className={styles.section}>
          <h4 className={styles.sectionTitle}>By Status</h4>
          <div className={styles.statusBars}>
            <div className={styles.statusRow}>
              <span className={styles.statusLabel}>Active</span>
              <div className={styles.statusBarContainer}>
                <div 
                  className={`${styles.statusBar} ${styles.statusActive}`}
                  style={{ width: `${stats.total > 0 ? (stats.byStatus.active / stats.total) * 100 : 0}%` }}
                ></div>
              </div>
              <span className={styles.statusCount}>{stats.byStatus.active}</span>
            </div>
            <div className={styles.statusRow}>
              <span className={styles.statusLabel}>Pending</span>
              <div className={styles.statusBarContainer}>
                <div 
                  className={`${styles.statusBar} ${styles.statusPending}`}
                  style={{ width: `${stats.total > 0 ? (stats.byStatus.pending / stats.total) * 100 : 0}%` }}
                ></div>
              </div>
              <span className={styles.statusCount}>{stats.byStatus.pending}</span>
            </div>
            <div className={styles.statusRow}>
              <span className={styles.statusLabel}>Suspended</span>
              <div className={styles.statusBarContainer}>
                <div 
                  className={`${styles.statusBar} ${styles.statusSuspended}`}
                  style={{ width: `${stats.total > 0 ? (stats.byStatus.suspended / stats.total) * 100 : 0}%` }}
                ></div>
              </div>
              <span className={styles.statusCount}>{stats.byStatus.suspended}</span>
            </div>
          </div>
        </div>

        {/* Program Participation Warning */}
        {stats.byProgramCount.none > 0 && (
          <div className={styles.warningBanner}>
            <FaExclamationCircle className={styles.warningIcon} />
            <div className={styles.warningContent}>
              <span className={styles.warningText}>
                <strong>{stats.byProgramCount.none}</strong> members are not in any program
              </span>
              <button className={styles.warningAction} onClick={handleNotifyInactive}>
                <FaBell /> Notify
              </button>
            </div>
          </div>
        )}

        {/* Recent Joins */}
        {stats.recentJoins.length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Recent Joins (Last 7 Days)</h4>
            <div className={styles.recentList}>
              {stats.recentJoins.map((member, index) => (
                <div key={member._id || index} className={styles.recentItem}>
                  <div className={styles.memberAvatar}>
                    {(member.user?.firstName || member.firstName || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className={styles.memberInfo}>
                    <span className={styles.memberName}>
                      {member.user?.firstName || member.firstName || 'Unknown'} {member.user?.lastName || member.lastName || ''}
                    </span>
                    <span className={styles.memberTime}>
                      {formatTimeAgo(member.joinedAt || member.createdAt)}
                    </span>
                  </div>
                  <span className={`${styles.memberStatus} ${styles[`status${(member.status || 'active').toLowerCase()}`]}`}>
                    {member.status || 'Active'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <button className={styles.footerLink} onClick={handleViewAllMembers}>
          View All Members <FaArrowRight />
        </button>
        <button className={styles.footerLink} onClick={handleInviteMembers}>
          Invite Members <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default ClubMembershipOverview;

